/**
 * One part's tariff_v2 result, laid out like the v1 "Tariff Analysis" tab
 * (HTSCard → Material Summary → Part Information) so users need no retraining.
 * v2 additions sit inside the same cards: the official description and the
 * schedule's rate on the primary card, the alternates' two-part justification
 * (always open, spec D13), the review flags, and Section 232/301 list status
 * with its conditions.
 */

import { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaChevronDown, FaChevronUp, FaExclamationTriangle } from 'react-icons/fa';
import { FaFileExcel, FaFilePdf } from 'react-icons/fa6';
import { FiInfo } from 'react-icons/fi';
import { IoCubeOutline } from 'react-icons/io5';
import { ReviewBanner, formatHtsCode, programSummary, rateText } from './shared';

const auth = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem('userToken')}` } });

export async function downloadFile(url, fallbackName) {
    try {
        const res = await axios.get(url, { ...auth(), responseType: 'blob' });
        const match = /filename="([^"]+)"/.exec(res.headers['content-disposition'] || '');
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(new Blob([res.data]));
        link.download = match ? match[1] : fallbackName;
        document.body.appendChild(link);
        link.click();
        link.remove();
    } catch {
        toast.error('Download failed');
    }
}

/** PDF + Excel buttons for a tariff_v2 export URL. `onDark` matches the green header of the file panel. */
export function ExportButtons({ url, name, onDark = false }) {
    const style = onDark ? 'p-2 hover:bg-white/10 rounded-lg transition-colors'
        : 'p-2 rounded-lg bg-white border text-[#1e5045] hover:bg-gray-50';
    return (
        <div className="flex gap-1">
            <button onClick={() => downloadFile(`${url}?type=pdf`, `${name}.pdf`)} title="Download PDF report" className={style}>
                <FaFilePdf className="w-6 h-6" />
            </button>
            <button onClick={() => downloadFile(`${url}?type=xlsx`, `${name}.xlsx`)} title="Export to Excel" className={style}>
                <FaFileExcel className="w-6 h-6" />
            </button>
        </div>
    );
}

export function ListedBanners({ programs }) {
    const { unknown, sec232, sec301 } = programSummary(programs);
    if (unknown) {
        return <div className="text-xs text-gray-500">Section 232 / 301 lists not loaded — membership unknown.</div>;
    }
    return (
        <>
            {[...sec301, ...sec232].map((p) => (
                <div key={p.program_id} className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex gap-3">
                    <FaExclamationTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div>
                        <div className="font-medium text-yellow-800">{p.label} — listed</div>
                        {p.conditions && <div className="text-sm text-yellow-700">{p.conditions}</div>}
                    </div>
                </div>
            ))}
        </>
    );
}

/** v1 "Alternative Classifications" rows; open by default because every alternate's justification must be visible (D13). */
export function AlternatesList({ alternates = [] }) {
    const [collapsed, setCollapsed] = useState(() => new Set());
    const toggle = (i) => setCollapsed((prev) => {
        const next = new Set(prev);
        next.has(i) ? next.delete(i) : next.add(i);
        return next;
    });
    return (
        <div>
            <div className="text-sm font-medium text-gray-700 mb-2">Alternative Classifications ({alternates.length})</div>
            {!alternates.length && (
                <div className="text-sm text-amber-700">No alternative code survived validation against the schedule.</div>
            )}
            <div className="space-y-2">
                {alternates.map((alt, idx) => {
                    const open = !collapsed.has(idx);
                    const { sec232, sec301 } = programSummary(alt.programs);
                    return (
                        <div key={alt.hts.code} className="bg-white border rounded-lg overflow-hidden">
                            <button type="button" onClick={() => toggle(idx)}
                                className="w-full flex justify-between items-center p-3 text-left hover:bg-gray-50 cursor-pointer">
                                <div className="flex gap-3 items-center">
                                    {open ? <FaChevronUp className="w-3 h-3 text-gray-400" /> : <FaChevronDown className="w-3 h-3 text-gray-400" />}
                                    <span className="font-mono font-medium">{formatHtsCode(alt.hts.code)}</span>
                                    <span className="text-sm text-gray-500">{rateText(alt.schedule)} duty</span>
                                </div>
                                <span className="text-sm text-gray-500">{alt.confidence}% confidence</span>
                            </button>
                            {open && (
                                <div className="px-3 pb-3 border-t border-gray-100 pt-3 space-y-2">
                                    <div className="text-sm text-gray-700">{alt.schedule.description}</div>
                                    {(sec301.length > 0 || sec232.length > 0) && (
                                        <div className="flex gap-2 flex-wrap">
                                            {sec301.map((p) => <span key={p.program_id} className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded">§301 listed</span>)}
                                            {sec232.map((p) => <span key={p.program_id} className="px-2 py-0.5 text-xs bg-orange-100 text-orange-800 rounded">§232 listed</span>)}
                                        </div>
                                    )}
                                    <div className="bg-gray-100 rounded-lg p-3">
                                        <div className="text-sm font-medium text-gray-700 mb-1">Why it was considered</div>
                                        <p className="text-sm text-gray-600">{alt.justification.why_plausible}</p>
                                        <div className="text-sm font-medium text-gray-700 mt-2 mb-1">Why it was not selected</div>
                                        <p className="text-sm text-gray-600">{alt.justification.why_not_selected}</p>
                                        <p className="text-xs text-gray-500 mt-1">Deciding fact: {alt.justification.distinguishing_fact}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/** The v1 HTSCard layout for a v2 classification block. */
export function HTSCardV2({ classification, title = 'Primary HTS Code' }) {
    const { primary, alternates = [] } = classification;
    return (
        <div className="space-y-4">
            <div className="bg-gradient-to-br from-green-700 to-green-900 p-6 rounded-xl text-white">
                <div className="text-sm opacity-80 mb-1">{title}</div>
                <div className="font-mono text-3xl font-bold text-orange-300">{formatHtsCode(primary.hts.code)}</div>
                <div className="text-sm opacity-90 mt-1">{primary.schedule.description}</div>
                <div className="flex gap-6 mt-3 flex-wrap">
                    <div><span className="text-sm opacity-80">General Duty:</span>
                        <span className="ml-2 font-semibold">{rateText(primary.schedule)}</span></div>
                    <div><span className="text-sm opacity-80">Confidence:</span>
                        <span className="ml-2 font-semibold">{primary.confidence}%</span></div>
                    {primary.code_status === 'repaired' && (
                        <div className="text-sm opacity-80">Suffix corrected from {primary.original_code}</div>
                    )}
                </div>
            </div>

            <ListedBanners programs={primary.programs} />

            <div className="bg-gray-100 rounded-lg p-4">
                <div className="text-sm font-medium text-gray-700 mb-1">Classification Rationale</div>
                <p className="text-sm text-gray-600">{primary.rationale}</p>
                {!!primary.reasoning_gri?.length && (
                    <ul className="text-xs text-gray-500 mt-2 space-y-0.5">
                        {primary.reasoning_gri.map((s, i) => (
                            <li key={i}><span className={s.applied ? 'font-semibold text-gray-700' : ''}>{s.rule}</span> — {s.note}</li>
                        ))}
                    </ul>
                )}
                <div className="text-xs text-gray-500 mt-2">{(primary.schedule.description_chain || []).join(' › ')}</div>
            </div>

            <AlternatesList alternates={alternates} />
        </div>
    );
}

/** v1 Material Summary card, without weight; Section 232/301 from the official lists. */
function MaterialSummaryV2({ facts, programs }) {
    const material = facts?.material || {};
    const { unknown, sec232, sec301 } = programSummary(programs);
    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-5">
                <h3 className="text-lg font-semibold text-[#1e5045] mb-4">Material Summary</h3>
                <div className="space-y-4">
                    <div className={`flex items-center justify-between p-3 rounded-lg ${sec232.length
                        ? 'bg-[#f26522]/10 border border-[#f26522]/30' : 'bg-[#f6f4ed]'}`}>
                        <div className="flex items-center gap-3">
                            {!!sec232.length && (
                                <span className="text-xs font-medium text-[#f26522] bg-[#f26522]/20 px-2 py-0.5 rounded">Sec 232</span>
                            )}
                            <div>
                                <div className="font-semibold text-gray-900">{material.as_stated || 'Material not stated on drawing'}</div>
                                <div className="text-xs text-gray-500">Category: {(material.family || 'unknown').replace(/_/g, ' ')}</div>
                            </div>
                        </div>
                    </div>
                    {!!sec301.length && (
                        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <FiInfo className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <div className="text-sm font-medium text-amber-800">HTS Code Listed for Section 301</div>
                                <div className="text-xs text-amber-600 mt-0.5">{sec301.map((p) => p.label).join('; ')} — applies if Country of Origin is China</div>
                            </div>
                        </div>
                    )}
                    {unknown && (
                        <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                            <span className="text-sm text-gray-600">Section 232 / 301 lists not loaded — exposure unknown</span>
                        </div>
                    )}
                    {!unknown && !sec232.length && !sec301.length && (
                        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="w-2 h-2 bg-green-500 rounded-full" />
                            <span className="text-sm text-green-700">HTS code is not on any loaded Section 232 or 301 list</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/** v1 Part Information card, without weight or purchase cost. */
function PartInformationV2({ part }) {
    const identity = part.identity || {};
    const facts = part.facts || {};
    const field = (label, value, wide = false) => (
        <div className={wide ? 'col-span-2' : ''}>
            <div className="text-sm text-gray-500">{label}</div>
            <div className="font-medium text-gray-900">{value || 'N/A'}</div>
        </div>
    );
    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <IoCubeOutline className="w-5 h-5 text-green-900" /> Part Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
                {field('Part Number', identity.part_number)}
                {field('Revision', identity.revision)}
                {field('Description', identity.title || identity.description, true)}
                {field('Form', (facts.form || '').replace(/_/g, ' '))}
                {field('Material Standard', facts.material?.standard)}
                {field('Function', facts.function_summary, true)}
                {field('Relevant Drawing Notes', (facts.drawing_notes_relevant || []).join(' · ') || 'None', true)}
            </div>
            {part.bom_table?.present && (
                <div className="mt-5 overflow-hidden border rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-50"><tr>
                            {['Item', 'Part Number', 'Description', 'Qty'].map((h) => (
                                <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                            ))}</tr></thead>
                        <tbody className="divide-y divide-gray-200">
                            {part.bom_table.rows.map((r, i) => (
                                <tr key={i}><td className="px-4 py-2">{r.item}</td><td className="px-4 py-2 font-mono">{r.part_number}</td>
                                    <td className="px-4 py-2">{r.description}</td><td className="px-4 py-2">{r.qty ?? '?'}</td></tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default function PartClassificationV2({ stored }) {
    const part = stored?.data;
    if (!part) return null;
    const c = part.classification;
    if (part.status === 'failed' || !c) {
        return (
            <div className="space-y-6">
                <div className="bg-[#f26522]/10 border border-[#f26522]/30 rounded-lg p-4 flex items-start gap-3">
                    <FaExclamationTriangle className="w-5 h-5 text-[#f26522] flex-shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-medium text-[#f26522]">This drawing could not be classified</p>
                        <p className="text-sm text-gray-700 mt-1">{(part.failure_reason || '').replace(/_/g, ' ')}{part.failure_detail ? ` — ${part.failure_detail}` : ''}</p>
                    </div>
                </div>
                <PartInformationV2 part={part} />
            </div>
        );
    }
    return (
        <div className="space-y-6">
            <ReviewBanner needsReview={c.needs_review} reasons={c.review_reasons} openQuestions={c.open_questions} />
            <HTSCardV2 classification={c} />
            <MaterialSummaryV2 facts={part.facts} programs={c.primary.programs} />
            <PartInformationV2 part={part} />
            <div className="text-xs text-gray-500">
                HTS schedule {part.provenance?.hts_schedule_revision}
                {part.verification ? ` · Verifier ${part.verification.agrees_with_primary ? 'agrees' : 'disagrees'}: ${part.verification.comment}` : ''}
            </div>
        </div>
    );
}
